/** Stable waypoint identities; screen-space thinning. Five is a floor when the visible route has room. */

export const OVERVIEW_TICKER_COUNT = 5;
export const MIN_SCREEN_PX = 88;

export type ScreenPt = { x: number; y: number };

export function screenDist(a: ScreenPt, b: ScreenPt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function routeScreenLength(points: ScreenPt[]): number {
  let len = 0;
  for (let i = 1; i < points.length; i++) {
    len += screenDist(points[i - 1]!, points[i]!);
  }
  return len;
}

/** Even index picks for reverse-geocode priority only — not map placement. */
export function parametricAnchorIndices(n: number, count = OVERVIEW_TICKER_COUNT): number[] {
  if (n <= 0) return [];
  if (n <= count) return Array.from({ length: n }, (_, i) => i);
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    out.push(Math.round((n - 1) * (i / (count - 1))));
  }
  return Array.from(new Set(out)).sort((a, b) => a - b);
}

/**
 * Keep a subset of the original waypoint indices.
 * Endpoints stay. Intermediates drop when screen-adjacent neighbors are closer than minPx.
 * Zooming in restores omitted original samples; it does not invent new route locations.
 */
export function thinTickerIndices(opts: {
  points: ScreenPt[];
  minPx?: number;
  minCount?: number;
  selectedIndex?: number;
  alertIndices?: number[];
}): number[] {
  const {
    points,
    minPx = MIN_SCREEN_PX,
    minCount = OVERVIEW_TICKER_COUNT,
    selectedIndex = -1,
    alertIndices = [],
  } = opts;
  const n = points.length;
  if (n <= 0) return [];
  if (n === 1) return [0];

  const routePx = routeScreenLength(points);
  const maxFit = Math.max(2, Math.floor(routePx / minPx) + 1);
  const targetMin = Math.min(n, minCount, maxFit);

  const keep = new Set<number>([0, n - 1]);

  const fits = (i: number): boolean => {
    for (const j of keep) {
      if (screenDist(points[i]!, points[j]!) < minPx) return false;
    }
    return true;
  };

  let last = 0;
  for (let i = 1; i < n - 1; i++) {
    if (screenDist(points[i]!, points[last]!) >= minPx && screenDist(points[i]!, points[n - 1]!) >= minPx) {
      keep.add(i);
      last = i;
    }
  }

  if (selectedIndex >= 0 && selectedIndex < n) keep.add(selectedIndex);
  for (const i of alertIndices) {
    if (i > 0 && i < n - 1 && fits(i)) keep.add(i);
  }

  while (keep.size < targetMin) {
    let best = -1;
    let bestScore = -1;
    for (let i = 1; i < n - 1; i++) {
      if (keep.has(i)) continue;
      let nearest = Infinity;
      for (const j of keep) {
        nearest = Math.min(nearest, screenDist(points[i]!, points[j]!));
      }
      if (nearest > bestScore) {
        bestScore = nearest;
        best = i;
      }
    }
    if (best < 0) break;
    keep.add(best);
  }

  return [...keep].sort((a, b) => a - b);
}
